import { useEffect, useState } from 'react'
import { api, type FormSettings, type FormSynonym } from '../lib/api.ts'
import { synonymsOf } from '../lib/formSettings.ts'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { EntityForm, Field, FieldGroup } from '../components/EntityForm.tsx'
import { Input } from '../components/Input.tsx'
import { Tag } from '../components/Tag.tsx'
import { useConfirm } from '../components/Confirm.tsx'
import { useToast } from '../components/Toast.tsx'
import { icons } from '../components/icons.ts'

/**
 * One pharmaceutical form, and every name it is found by (#607, ADR 0041).
 *
 * The product page's shape, for the same reason: a name waiting for approval
 * is judged best beside the names the form already has. The built-in ones are
 * shown and cannot be changed -- they are code, and the reader of the
 * prescription is told them -- and the server refuses a name that is a
 * built-in one of another form, saying which.
 */

const STATUS: Readonly<
  Record<FormSynonym['status'], { label: string; tone: 'success' | 'warning' | 'neutral' }>
> = {
  approved: { label: 'Aprovado', tone: 'success' },
  pending: { label: 'Aguardando aprovação', tone: 'warning' },
  rejected: { label: 'Descartado', tone: 'neutral' },
}

type Action = 'approve' | 'reject' | 'remove'

export function Form({
  accountId,
  form,
  onBack,
}: {
  accountId: string | undefined
  form: string
  onBack: () => void
}) {
  const [settings, setSettings] = useState<FormSettings | undefined>()
  const [busy, setBusy] = useState<string | undefined>()
  const [error, setError] = useState<string | undefined>()
  const toast = useToast()
  const confirm = useConfirm()

  async function load() {
    if (accountId === undefined) return
    try {
      setSettings(await api.formSettings(accountId))
      setError(undefined)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao carregar a forma')
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, form])

  const entry = settings?.forms.find((one) => one.id === form)

  async function act(synonym: FormSynonym, action: Action) {
    if (accountId === undefined) return
    if (action === 'remove') {
      const sure = await confirm({
        title: `Remover "${synonym.name}"?`,
        message:
          synonym.status === 'rejected'
            ? 'O nome volta a poder ser proposto por uma resposta numa conferência.'
            : 'A receita deixa de reconhecer esta forma por esse nome nas próximas conferências.',
        confirmLabel: 'Remover',
        tone: 'danger',
      })
      if (!sure) return
    }
    setBusy(synonym.id)
    try {
      if (action === 'remove') await api.removeFormSynonym(accountId, synonym.id)
      else await api.decideFormSynonym(accountId, synonym.id, action)
      await load()
      toast.show(
        `"${synonym.name}" ${action === 'approve' ? 'aprovado' : action === 'reject' ? 'descartado' : 'removido'}`,
        { tone: 'success' },
      )
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao registrar', { tone: 'error' })
    } finally {
      setBusy(undefined)
    }
  }

  if (error !== undefined || (settings !== undefined && entry === undefined)) {
    return (
      <div className="flex flex-col items-start gap-3 p-5">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Voltar
        </Button>
        <h1 className="text-tH4 text-text-strong">Forma não encontrada</h1>
        {error !== undefined && <p className="text-tP text-text-weak">{error}</p>}
      </div>
    )
  }

  if (settings === undefined || entry === undefined || accountId === undefined) {
    return (
      <p className="text-tP text-text-weak p-5" role="status" aria-live="polite">
        Carregando…
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Voltar
        </Button>
        <h1 className="text-tH4 text-text-strong">{entry.label}</h1>
      </div>

      <EntityForm description="Como esta forma é reconhecida na receita desta farmácia, na comparação com a forma aviada.">
        <Field label="Forma" value={entry.label} />
        <FieldGroup
          label="Nomes fixos"
          helperText="Valem para todas as farmácias e também orientam a leitura da receita, por isso não mudam aqui."
        >
          <div className="flex flex-wrap gap-1">
            {entry.builtInNames.map((name) => (
              <Tag key={name} variant="stroke">
                {name}
              </Tag>
            ))}
          </div>
        </FieldGroup>
        <FieldGroup
          label="Sinônimos desta farmácia"
          helperText="Valem só na comparação da forma, a partir da próxima conferência. Os propostos ao responder “É um sinônimo” numa divergência de forma só passam a valer depois de aprovados; aprove apenas se for a mesma forma. Descartar impede que o nome volte a ser proposto."
        >
          <DataTable
            columns={columns(busy, act)}
            rows={synonymsOf(settings.synonyms, form)}
            rowKey={(one) => one.id}
            empty="Nenhum sinônimo desta farmácia para esta forma."
            footer={<AddName accountId={accountId} form={form} onAdded={() => void load()} />}
          />
        </FieldGroup>
      </EntityForm>
    </div>
  )
}

function columns(
  busy: string | undefined,
  act: (synonym: FormSynonym, action: Action) => void,
): ReadonlyArray<Column<FormSynonym>> {
  return [
    {
      key: 'name',
      header: 'Nome',
      render: (one) => (
        <span
          className={
            one.status === 'pending'
              ? 'font-semibold'
              : one.status === 'rejected'
                ? 'text-text-weak line-through'
                : ''
          }
        >
          {one.name}
        </span>
      ),
    },
    {
      key: 'origin',
      header: 'Origem',
      className: 'text-text-weak',
      render: (one) =>
        one.proposedFrom === undefined ? 'Adicionado aqui' : 'Resposta numa conferência',
    },
    { key: 'by', header: 'Por', className: 'text-text-weak', render: (one) => one.proposedBy },
    {
      key: 'status',
      header: 'Situação',
      render: (one) => <Tag tone={STATUS[one.status].tone}>{STATUS[one.status].label}</Tag>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-px whitespace-nowrap text-right',
      headerClassName: 'w-px',
      render: (one) =>
        one.status === 'pending' ? (
          <span className="flex justify-end gap-2">
            <Button
              size="small"
              variant="secondary"
              tone="brand"
              disabled={busy === one.id}
              onClick={() => act(one, 'approve')}
            >
              Aprovar
            </Button>
            <Button
              size="small"
              variant="secondary"
              tone="neutral"
              disabled={busy === one.id}
              onClick={() => act(one, 'reject')}
            >
              Descartar
            </Button>
          </span>
        ) : (
          <Button
            size="small"
            variant="secondary"
            tone="danger"
            aria-label={`Remover "${one.name}"`}
            title="Remover"
            leadingIcon={icons.remove}
            disabled={busy === one.id}
            onClick={() => act(one, 'remove')}
          />
        ),
    },
  ]
}

/**
 * A name typed straight into the table's last row, approved: the person
 * typing it has already decided. Refused, with the server's reason, when it is
 * a built-in name or the account's name for another form.
 */
function AddName({
  accountId,
  form,
  onAdded,
}: {
  accountId: string
  form: string
  onAdded: () => void
}) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const ready = name.trim() !== ''

  async function add() {
    if (!ready) return
    setBusy(true)
    try {
      await api.addFormSynonym(accountId, name.trim(), form)
      toast.show(`"${name.trim()}" adicionado`, { tone: 'success' })
      setName('')
      onAdded()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao adicionar', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  // Not a <form>: this row lives inside the entity form, and a form inside a
  // form is invalid HTML. Enter on the box does what the button does.
  return (
    <div className="flex items-center gap-2">
      <Input
        size="small"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return
          event.preventDefault()
          void add()
        }}
        placeholder="Adicionar sinônimo…"
        aria-label="Novo sinônimo"
        containerClassName="max-w-[24rem]"
        disabled={busy}
      />
      <Button
        type="button"
        size="small"
        variant="secondary"
        tone="brand"
        disabled={busy || !ready}
        onClick={() => void add()}
      >
        {busy ? 'Adicionando…' : 'Adicionar'}
      </Button>
    </div>
  )
}
